import { z } from 'zod';
/**
 * What each vendor gives free, and what goes beyond it (ADR-0095).
 *
 * The catalogue is the default until a superadmin records the plan the
 * organisation actually holds. It is written from the vendor's own page on
 * the day in `verifiedOn`, and each unit names where that was read, so a
 * figure that moves can be checked rather than trusted. Where two of the
 * vendor's pages disagree, the stricter reading is here and the other is
 * in `note`.
 *
 * A unit is the vendor's own: Customer.io bills profiles, Hunter credits,
 * Cloudflare browser seconds. Nothing here is converted into another
 * vendor's terms.
 */
export const VENDOR_PERIODS = ['day', 'month', 'plan'];
/** Warn at this share of what is free, and stop at that one (ADR-0095). */
export const DEFAULT_WARN_PCT = 80;
export const DEFAULT_STOP_PCT = 95;
export const VENDOR_CATALOGUE = [
    {
        vendor: 'customerio',
        name: 'Customer.io',
        plan: 'Startup Program: 12 months, 30,000 profiles',
        verifiedOn: '2026-09-24',
        policy: 'Customer.io sends no email: mail goes out as Google, from the platform (ADR-0095).',
        units: [
            {
                unit: 'profiles',
                label: 'profiles',
                period: 'month',
                included: 30_000,
                pricePerUnitUsd: 0.007,
                countedBy: 'records',
                source: 'https://docs.customer.io/accounts/billing/how-we-bill/',
                note: 'People and objects. Every profile that existed at any time in the billing month counts, deleted ones included (high watermark).',
            },
            {
                unit: 'emails',
                label: 'emails sent',
                period: 'month',
                included: 0,
                pricePerUnitUsd: 0.00012,
                countedBy: 'vendor',
                source: 'https://docs.customer.io/accounts/billing/how-we-bill/',
                note: 'Zero by policy: any email Customer.io reports as sent pauses Customer.io.',
            },
        ],
    },
    {
        vendor: 'hunter',
        name: 'Hunter',
        plan: 'Free: 50 credits a month',
        verifiedOn: '2026-09-24',
        units: [
            {
                unit: 'credits',
                label: 'credits',
                period: 'month',
                included: 50,
                pricePerUnitUsd: null,
                countedBy: 'gateway',
                source: 'https://help.hunter.io/en/articles/11060999-what-s-included-in-hunter-s-free-plan',
                note: 'A find or a domain search is one credit; a verification is half of one. More is a paid plan, not a per-credit price.',
            },
        ],
    },
    {
        vendor: 'google_places',
        name: 'Google Places',
        plan: 'Free monthly cap per SKU',
        verifiedOn: '2026-09-28',
        units: [
            {
                unit: 'text_search_ids',
                label: 'Text Search (IDs only) calls',
                period: 'month',
                included: 10_000,
                pricePerUnitUsd: null,
                countedBy: 'gateway',
                source: 'https://developers.google.com/maps/billing-and-pricing/pricing',
                note: 'Text Search Essentials (IDs Only) is unlimited at Google; 10,000 is our own line, so a manifest that asks for more fields cannot bill unnoticed.',
            },
            {
                unit: 'place_details',
                label: 'Place Details calls',
                period: 'month',
                included: 1_000,
                pricePerUnitUsd: null,
                countedBy: 'gateway',
                source: 'https://developers.google.com/maps/documentation/places/web-service/usage-and-billing',
                note: "Place Details Enterprise: websiteUri, rating, userRatingCount and priceLevel bill there, 1,000 free, then $20 per 1,000. Pro's 5,000 is for a field mask without them.",
            },
        ],
    },
    {
        // The territory map (ADR-0137): Google's own script in the reader's
        // browser, under the public key Amendment 0010 allows. A load is counted
        // when the platform hands the key out, and the key is withheld at the
        // stop line, so the page draws the grid alone rather than spend.
        vendor: 'google_maps_js',
        name: 'Google Maps JavaScript API',
        plan: 'Free monthly cap per SKU',
        verifiedOn: '2026-09-26',
        units: [
            {
                unit: 'map_loads',
                label: 'Dynamic Maps loads',
                period: 'month',
                included: 10_000,
                pricePerUnitUsd: null,
                countedBy: 'records',
                source: 'https://developers.google.com/maps/billing-and-pricing/pricing',
                note: 'Essentials SKU cap. Counted each time a territory map is opened and the key handed out; the key is withheld at the stop line.',
            },
        ],
    },
    {
        vendor: 'cloudflare_browser',
        name: 'Cloudflare Browser Rendering',
        plan: 'Workers Free: 10 minutes a day',
        verifiedOn: '2026-09-24',
        units: [
            {
                unit: 'browser_seconds',
                label: 'browser seconds',
                period: 'day',
                included: 600,
                pricePerUnitUsd: 0.09 / 3600,
                countedBy: 'gateway',
                source: 'https://developers.cloudflare.com/browser-run/pricing/',
                note: 'Ten seconds a call is held against the line before the call, then put right by the time Cloudflare reports in the response (X-Browser-Ms-Used), once the call is made. Workers Paid includes 10 hours a month instead; record that plan if it is the one held.',
            },
        ],
    },
    {
        vendor: 'apollo',
        name: 'Apollo',
        plan: 'Not recorded: nothing is spent until a superadmin records the plan held',
        verifiedOn: '2026-09-26',
        policy: 'A second source for the buyer at a company above its profile’s fit threshold, never a list to mail (ADR-0130).',
        units: [
            {
                unit: 'credits',
                label: 'credits',
                period: 'month',
                included: 0,
                pricePerUnitUsd: null,
                countedBy: 'gateway',
                source: 'https://docs.apollo.io/docs/api-pricing',
                note: 'Zero until the plan is recorded on Plans and usage, so the default stops every call rather than guessing a free line (ADR-0095).',
            },
        ],
    },
    {
        vendor: 'google_vertex',
        name: 'Google Vertex (Gemini)',
        plan: 'Pay as you go: nothing free',
        verifiedOn: '2026-09-24',
        units: [
            {
                unit: 'usd',
                label: 'US dollars',
                period: 'day',
                included: 0,
                pricePerUnitUsd: 1,
                countedBy: 'cost_ledger',
                source: 'https://cloud.google.com/vertex-ai/generative-ai/pricing',
                note: 'Paid from the first call. Allowed up to the budget a superadmin records, held over a trailing 24 hours; this row reads the UTC day. Until one is recorded, the deployment’s COST_BUDGETS.',
            },
        ],
    },
    {
        vendor: 'google_workspace',
        name: 'Google Workspace',
        plan: 'In the Workspace subscription',
        verifiedOn: '2026-09-24',
        units: [
            {
                unit: 'gmail_sends',
                label: 'Gmail sends per person',
                period: 'day',
                included: 2_000,
                pricePerUnitUsd: null,
                countedBy: 'records',
                limitOnly: true,
                source: 'https://support.google.com/a/answer/166852',
                note: 'A sending limit, not a price, so no paid use can be allowed past it. Google counts it per box over 24 hours; this is the busiest box, counted from what the platform sent (mail sent in Gmail directly is not here). An agent’s send waits at the stop share. Mail, Chat, Calendar and Drive are in the subscription.',
            },
        ],
    },
];
export function vendorDefault(vendor) {
    return VENDOR_CATALOGUE.find((v) => v.vendor === vendor);
}
export function unitDefault(vendor, unit) {
    return vendorDefault(vendor)?.units.find((u) => u.unit === unit);
}
/**
 * Where a unit stops, in the unit (ADR-0095).
 *
 * With no paid use allowed, at the stop share of what is free: the margin
 * is there for what the vendor counts and the platform cannot see. With
 * paid use allowed, at what is free plus what was allowed — the person who
 * allowed it chose the figure, and there is no margin to keep.
 */
export function stopLine(allowance) {
    if (allowance.paidAllowed > 0)
        return allowance.included + allowance.paidAllowed;
    return Math.floor((allowance.included * allowance.stopPct) / 100);
}
export function warnLine(allowance) {
    return Math.floor((allowance.included * allowance.warnPct) / 100);
}
/**
 * The day of a month a plan's months turn over on: the day it started,
 * held to the month's length, so a plan that started on the 31st turns
 * over on the 30th in September and the 28th or 29th in February.
 */
function anchorIn(year, month, anchorDay) {
    const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(anchorDay, last)));
}
/** The plan's day of the month, or 1 — the calendar month — when no plan is recorded. */
function anchorDay(planStartsOn) {
    const day = planStartsOn ? Number(planStartsOn.slice(8, 10)) : 1;
    return Number.isInteger(day) && day >= 1 && day <= 31 ? day : 1;
}
/**
 * The start of the period `at` falls in, in UTC, as vendors bill. A month
 * runs from the plan's own start day when one is recorded (phase-20: a
 * vendor billing from the 15th counts from the 15th), and from the 1st
 * otherwise.
 */
export function periodStart(period, at, planStartsOn) {
    if (period === 'day')
        return new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
    if (period === 'month') {
        const day = anchorDay(planStartsOn);
        const here = anchorIn(at.getUTCFullYear(), at.getUTCMonth(), day);
        return here.getTime() <= at.getTime() ? here : anchorIn(at.getUTCFullYear(), at.getUTCMonth() - 1, day);
    }
    return planStartsOn ? new Date(`${planStartsOn}T00:00:00Z`) : new Date(0);
}
/** When the period after `at` begins: the moment a stopped unit opens again. Null for a whole plan. */
export function periodEnd(period, at, planStartsOn) {
    if (period === 'day')
        return new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate() + 1));
    if (period === 'month') {
        const start = periodStart('month', at, planStartsOn);
        return anchorIn(start.getUTCFullYear(), start.getUTCMonth() + 1, anchorDay(planStartsOn));
    }
    return null;
}
/**
 * What one call spends, as its signed manifest says (ADR-0095). `perCall`
 * zero means the call belongs to the vendor — paused with it, stopped when
 * its plan ends — and is counted elsewhere, as Customer.io's profiles are.
 */
export const meterSchema = z.object({
    vendor: z.string().regex(/^[a-z][a-z_]*$/),
    unit: z.string().regex(/^[a-z][a-z_]*$/),
    perCall: z.number().min(0).max(1_000),
    /** Add what the response reports in this header, divided by `headerDivisor`, instead of `perCall`. */
    fromHeader: z.string().regex(/^[A-Za-z0-9-]+$/).optional(),
    headerDivisor: z.number().positive().optional(),
});
/** The catalogue's limits, before a superadmin has recorded any. Limits that are not prices are not stopped here. */
export function defaultLimit(vendor, unit) {
    const u = unitDefault(vendor, unit);
    if (!u)
        return null;
    return {
        vendor,
        unit,
        period: u.period,
        stop: u.limitOnly ? Number.MAX_SAFE_INTEGER : stopLine({ included: u.included, stopPct: DEFAULT_STOP_PCT, paidAllowed: 0 }),
        stopAfterPlan: u.limitOnly ? Number.MAX_SAFE_INTEGER : 0,
        planEndsOn: null,
    };
}
/** The line in force on `at`: the plan's, or once its last day has passed, only paid use. */
export function effectiveStop(limit, at) {
    const ended = limit.planEndsOn !== null && at.toISOString().slice(0, 10) > limit.planEndsOn;
    return ended ? limit.stopAfterPlan : limit.stop;
}
//# sourceMappingURL=vendor-plans.js.map